﻿package org.zq.pixelset.ui

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.sp

enum class FooterDialog {
    FormatSupport,
    Faq,
    PrivacyPolicy,
}

@Composable
fun FooterInfoDialog(
    dialog: FooterDialog,
    onDismiss: () -> Unit,
) {
    val (title, body) = when (dialog) {
        FooterDialog.FormatSupport -> "格式支持" to FORMAT_SUPPORT_TEXT
        FooterDialog.Faq -> "常见问题" to FAQ_TEXT
        FooterDialog.PrivacyPolicy -> "隐私政策" to PRIVACY_TEXT
    }
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            PixelSetText(text = title, fontWeight = FontWeight.SemiBold)
        },
        text = {
            Column(Modifier.verticalScroll(rememberScrollState())) {
                PixelSetText(
                    text = body,
                    style = MaterialTheme.typography.bodyMedium,
                    fontSize = 14.sp,
                    lineHeight = 22.sp,
                )
            }
        },
        confirmButton = {
            TextButton(onClick = onDismiss) {
                PixelSetText(text = "知道了")
            }
        },
    )
}

private val FORMAT_SUPPORT_TEXT = """
支持的输入格式：
• JPEG / JPG
• PNG
• WebP
• GIF
• BMP

支持的输出格式：
• PNG（无损，适合图标与透明图）
• JPG（有损压缩，适合照片）
• WebP（体积更小，现代浏览器均支持）

缩放模式：
• 适应 — 保持比例，完整显示在目标尺寸内（可能留白）
• 填充 — 保持比例，裁剪以填满目标尺寸
• 拉伸 — 强制拉伸至目标宽高

所有处理均在本地完成，不会上传到服务器。
""".trimIndent()

private val FAQ_TEXT = """
如何上传图片？
点击左侧上传区可一次选择多张图片；在桌面版或网页版中，也可将图片拖拽到上传区域。上传后可在列表中点击切换要编辑的图片。

图片会上传到服务器吗？
不会。PixelSet 在您的设备本地完成解码、缩放与编码，处理后的文件也保存在本地，不会上传到任何服务器。

如何批量处理？
上传多张图片后，点击「处理全部」即可按当前设置依次处理所有图片；若只需处理当前选中的一张，请点击「处理当前」或「立即处理」。

如何批量下载？
处理多张图片后，点击「下载 ZIP」可将全部结果打包为一个 zip 文件下载；仅一张时直接下载单文件。

什么是自动预览？
开启后，修改宽高、格式、裁剪等参数时会自动处理全部已上传图片并更新对比预览（约 0.4 秒防抖）。

如何对比处理效果？
处理完成后，拖动预览区中间的滑块：左侧为原图，右侧为处理后效果。

压缩质量滑块有什么用？
对 JPG 和 WebP 有效，数值越高画质越好、文件越大；PNG 为无损格式，不受此滑块影响。

为什么文件体积反而变大了？
若输出为 PNG，或质量设置较高，体积可能大于原图。可尝试 JPG/WebP 并降低质量百分比。

批量处理可以取消吗？
可以。点击「处理全部」后，进度旁会出现「取消」按钮，可随时中止剩余任务。

参数会记住吗？
会。宽高、格式、自动预览开关及高级编辑（旋转、裁剪、色彩）等设置会在本地保存，下次打开自动恢复。

高级编辑包含什么？
支持 90° 旋转、四边裁剪边距、亮度/对比度/饱和度调节，并随「处理全部」一并应用。

每张图的参数一样吗？
每张图有独立参数。切换左侧列表中的图片可分别设置尺寸、裁剪与输出；新上传的图片会套用当前界面上的默认值。

什么是「仅压缩」？
保持裁剪/旋转后的原始分辨率，只转换格式并调节质量，适合在不改尺寸的情况下缩小体积。

大图会自动处理吗？
超过约 1200 万像素或单边 6000px 的图片会在加载时自动缩小并提示，避免内存不足。
""".trimIndent()

private val PRIVACY_TEXT = """
PixelSet 隐私政策

最后更新：2024 年

1. 本地处理
本应用的所有图片处理（包括读取、缩放、格式转换与导出）均在您的设备本地完成。我们不会将您的图片上传至任何服务器。

2. 数据收集
我们不收集、存储或传输您的图片内容、文件名或任何可识别个人身份的信息。

3. 网络访问
「源代码」链接会在您主动点击时打开外部网页；除该操作外，应用核心功能无需联网即可使用。

4. 第三方服务
本应用不集成第三方分析或广告 SDK。

5. 联系我们
如有隐私相关问题，请通过项目仓库提交 Issue。

使用本应用即表示您同意上述政策。
""".trimIndent()
